import { useState, useImperativeHandle, forwardRef } from 'react';
import { CustomSelect, DateTimePicker } from '@/components';
import { TimePickerInput } from '../../appointments/components/TimePickerInput';
import { INTERVAL_UNITS } from '../../utils/intervalUtils';

const SEND_MODES = [
  { value: 'send_now', label: 'Send Now', shortLabel: 'Send' },
  { value: 'scheduled', label: 'Schedule Once', shortLabel: 'Schedule' },
  { value: 'interval', label: 'Interval / Recurring', shortLabel: 'Interval' },
  { value: 'yearly_date', label: 'Yearly date', shortLabel: 'Yearly' },
];

const IMPORT_MODE_COPY = {
  scheduled: {
    label: 'Schedule all together',
    hint: 'Send to all contacts at the same date and time.',
  },
  yearly_date: {
    label: 'Per-contact date',
    hint: 'Each contact gets the message on their own event_date every year.',
  },
  interval: {
    label: 'Recurring interval',
    hint: 'Send on a repeat to all contacts.',
  },
};

const inputClassName =
  'w-full h-11 px-4 rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium placeholder:text-[#868686] focus:outline-none focus:border-primary transition-colors';

const selectButtonClassName =
  'w-full h-full flex items-center justify-between rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium focus:outline-none focus:border-primary transition-all px-4';

const labelClassName =
  'text-xs font-semibold text-text-secondary uppercase tracking-wider';

function defaultSerializeDateTime(date) {
  return date.toISOString();
}

export const TemplateScheduleOptions = forwardRef(
  function TemplateScheduleOptions(
    {
      disabled = false,
      onModeChange,
      allowedModes,
      initialMode,
      serializeDateTime = defaultSerializeDateTime,
      namePlaceholder = 'Optional',
      alwaysShowSharedFields = false,
      showIntervalDates = true,
      modeButtonClassName = '',
      initialStopOnReply = false,
      importLabels = false,
    },
    ref,
  ) {
    const modes = SEND_MODES.filter((mode) => {
      if (
        mode.value === 'yearly_date' &&
        !allowedModes?.includes('yearly_date')
      ) {
        return false;
      }
      return !allowedModes || allowedModes.includes(mode.value);
    });
    const resolvedInitial =
      initialMode && modes.some((mode) => mode.value === initialMode)
        ? initialMode
        : modes[0]?.value || 'send_now';

    const [sendMode, setSendMode] = useState(resolvedInitial);

    const handleModeChange = (mode) => {
      setSendMode(mode);
      onModeChange?.(mode);
    };
    const [name, setName] = useState('');
    const [scheduledDateTime, setScheduledDateTime] = useState(null);
    const [intervalValue, setIntervalValue] = useState(1);
    const [intervalUnit, setIntervalUnit] = useState('days');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [maxAttempts, setMaxAttempts] = useState('');
    const [stopOnReply, setStopOnReply] = useState(initialStopOnReply === true);
    const [yearlyTime, setYearlyTime] = useState('');

    const validate = () => {
      if (sendMode === 'send_now') {
        return { ok: true };
      }

      if (sendMode === 'scheduled') {
        if (!scheduledDateTime) {
          return {
            ok: false,
            message: 'Please select a schedule date & time.',
          };
        }
        if (scheduledDateTime.getTime() <= Date.now()) {
          return {
            ok: false,
            message: 'Scheduled date & time must be in the future.',
          };
        }
        return { ok: true };
      }

      if (sendMode === 'interval') {
        const value = Number(intervalValue);
        if (!Number.isFinite(value) || value < 1) {
          return {
            ok: false,
            message: 'Interval value must be at least 1.',
          };
        }
        if (showIntervalDates) {
          if (!startDate) {
            return { ok: false, message: 'Please select a start date & time.' };
          }
          if (startDate.getTime() <= Date.now()) {
            return {
              ok: false,
              message: 'Start date & time must be in the future.',
            };
          }
          if (endDate && endDate.getTime() <= startDate.getTime()) {
            return {
              ok: false,
              message: 'End date must be greater than start date.',
            };
          }
        }
        return { ok: true };
      }

      if (sendMode === 'yearly_date') {
        if (!yearlyTime) {
          return { ok: false, message: 'Please select a send time.' };
        }
        return { ok: true };
      }

      return { ok: false, message: 'Invalid send mode.' };
    };

    const yearlyScheduledDateTime = () => {
      const [hours, minutes] = yearlyTime.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return serializeDateTime(date);
    };

    const getSchedulePayload = () => {
      if (sendMode === 'send_now') {
        return {
          sendMode: 'send_now',
          schedule: {
            name: name.trim() || undefined,
            stop_on_reply: stopOnReply,
          },
        };
      }

      const base = {
        name: name.trim() || undefined,
        stop_on_reply: stopOnReply,
      };

      // UI keeps value "scheduled"; bulk API expects sending_mode "schedule_once"
      if (sendMode === 'scheduled') {
        return {
          sendMode: 'scheduled',
          schedule: {
            ...base,
            scheduled_date_time: serializeDateTime(scheduledDateTime),
          },
        };
      }

      if (sendMode === 'yearly_date') {
        return {
          sendMode: 'yearly_date',
          schedule: {
            ...base,
            scheduled_date_time: yearlyScheduledDateTime(),
          },
        };
      }

      const payload = {
        ...base,
        interval_value: Number(intervalValue),
        interval_unit: intervalUnit,
      };

      if (showIntervalDates) {
        payload.start_date = serializeDateTime(startDate);
        if (endDate) {
          payload.end_date = serializeDateTime(endDate);
        }
      }

      const attempts = Number(maxAttempts);
      if (maxAttempts !== '' && Number.isFinite(attempts) && attempts >= 0) {
        payload.max_attempts = attempts;
      }

      return {
        sendMode: 'interval',
        schedule: payload,
      };
    };

    useImperativeHandle(ref, () => ({
      sendMode,
      validate,
      getSchedulePayload,
      isScheduleValid: () => validate().ok,
    }));

    const showNameOnSendNow = alwaysShowSharedFields && sendMode === 'send_now';

    const scheduleNameField = (
      <div className="flex flex-col gap-2 sm:col-span-2">
        <label className={labelClassName}>Schedule Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={namePlaceholder}
          disabled={disabled}
          className={inputClassName}
        />
      </div>
    );

    const stopOnReplyToggle = (
      <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
        <input
          type="checkbox"
          checked={stopOnReply}
          onChange={(e) => setStopOnReply(e.target.checked)}
          disabled={disabled}
          className="peer sr-only"
        />
        <div
          className={`w-5 h-5 rounded-[6px] border flex items-center justify-center transition-all shrink-0 ${
            stopOnReply
              ? 'bg-primary border-primary'
              : 'border-[#868686] bg-transparent'
          }`}
        >
          {stopOnReply && (
            <svg
              className="w-3 h-3 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>
        <span className="text-sm font-medium text-[#1F2241]">
          Stop on customer reply
        </span>
      </label>
    );

    return (
      <div className="w-full flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          {/* <label className={labelClassName}>Sending Mode</label> */}
          {modes.length > 1 && (
            <div className="flex items-center gap-1.5 sm:gap-2 w-full">
              {modes.map((mode) => {
                const active = sendMode === mode.value;
                const label = importLabels
                  ? IMPORT_MODE_COPY[mode.value]?.label || mode.label
                  : mode.label;
                return (
                  <button
                    key={mode.value}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleModeChange(mode.value)}
                    className={`flex-1 min-w-0 min-h-10 h-auto py-2 px-1 sm:px-2.5 rounded-xl text-xs sm:text-sm font-semibold border transition-colors focus:outline-none flex items-center justify-center text-center ${modeButtonClassName} ${
                      active
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-[#1F2241] border-[#EBF4EA] hover:border-primary/40'
                    } disabled:opacity-50`}
                  >
                    <span
                      className={
                        importLabels ? 'whitespace-normal' : 'truncate'
                      }
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {modes.length > 1 &&
            importLabels &&
            IMPORT_MODE_COPY[sendMode]?.hint && (
              <p className="text-sm text-text-secondary">
                {IMPORT_MODE_COPY[sendMode].hint}
              </p>
            )}
        </div>

        {showNameOnSendNow && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {scheduleNameField}
            <div className="sm:col-span-2">{stopOnReplyToggle}</div>
          </div>
        )}

        {sendMode === 'scheduled' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {scheduleNameField}
            <div className="flex flex-col gap-2 sm:col-span-2">
              <label className={labelClassName}>
                Date & Time <span className="text-red-500">*</span>
              </label>
              <DateTimePicker
                selected={scheduledDateTime}
                onChange={setScheduledDateTime}
                mode="datetime"
                allowPastDates={false}
                disablePastTime={true}
                placeholderText="Select schedule date & time"
                disabled={disabled}
                className={inputClassName}
              />
            </div>
            {importLabels && (
              <p className="sm:col-span-2 text-sm text-text-secondary">
                All contacts will receive this message on this date and time.
              </p>
            )}
            <div className="sm:col-span-2">{stopOnReplyToggle}</div>
          </div>
        )}

        {sendMode === 'interval' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {scheduleNameField}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 sm:col-span-2">
              <div className="flex flex-col gap-2 min-w-0">
                <label className={`${labelClassName} truncate`}>
                  Interval Value <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={intervalValue}
                  onChange={(e) => setIntervalValue(e.target.value)}
                  disabled={disabled}
                  className={`${inputClassName} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                />
              </div>
              <div className="flex flex-col gap-2 min-w-0">
                <label className={`${labelClassName} truncate`}>
                  Interval Unit <span className="text-red-500">*</span>
                </label>
                <CustomSelect
                  value={intervalUnit}
                  onChange={setIntervalUnit}
                  options={INTERVAL_UNITS}
                  disabled={disabled}
                  className="w-full h-11"
                  buttonClassName={selectButtonClassName}
                  textSizeClass="text-sm"
                  align="center"
                />
              </div>
            </div>
            {showIntervalDates && (
              <>
                <div className="flex flex-col gap-2">
                  <label className={labelClassName}>
                    Start Date & Time <span className="text-red-500">*</span>
                  </label>
                  <DateTimePicker
                    selected={startDate}
                    onChange={setStartDate}
                    mode="datetime"
                    allowPastDates={false}
                    disablePastTime={true}
                    placeholderText="Select start date & time"
                    disabled={disabled}
                    className={inputClassName}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className={labelClassName}>End Date</label>
                  <DateTimePicker
                    selected={endDate}
                    onChange={setEndDate}
                    mode="datetime"
                    allowPastDates={false}
                    disablePastTime={true}
                    minDate={startDate}
                    placeholderText="Optional"
                    disabled={disabled}
                    className={inputClassName}
                  />
                </div>
              </>
            )}
            <div className="flex flex-col gap-2">
              <label className={labelClassName}>Max Attempts</label>
              <input
                type="number"
                min="0"
                value={maxAttempts}
                onChange={(e) => setMaxAttempts(e.target.value)}
                placeholder="Optional"
                disabled={disabled}
                className={inputClassName}
              />
            </div>
            <div className="sm:col-span-2 flex items-end">
              {stopOnReplyToggle}
            </div>
          </div>
        )}

        {sendMode === 'yearly_date' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {scheduleNameField}
            <div className="flex flex-col gap-2 sm:col-span-2">
              <label className={labelClassName}>
                Send Time <span className="text-red-500">*</span>
              </label>
              <TimePickerInput
                value={yearlyTime}
                onChange={setYearlyTime}
                disabled={disabled}
                placeholder="Select send time"
              />
            </div>
            <p className="sm:col-span-2 text-sm text-text-secondary">
              Each contact receives this reminder yearly on their event_date
              from the uploaded sheet, at the selected time.
            </p>
            <div className="sm:col-span-2">{stopOnReplyToggle}</div>
          </div>
        )}
      </div>
    );
  },
);
